import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, PlayCircle, Monitor, ShieldCheck, Zap, Sparkles, Wrench,
  GitCompareArrows, FileCode2, FolderTree, FolderOpen, Eye, Wand2, Star,
  Users, Lock, ChevronDown, Check, Minus, Globe, Cpu, Download, Undo2, Apple, X, Play,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

// Flip to true once the Mac App Store build ships — everything below updates.
const MAC_RELEASED = false;
const MAC_APP_STORE_URL = "https://apps.apple.com/app/rename-studio-ai";
const MAC_CTA = MAC_RELEASED ? "Download for Mac" : "Preview Mac App";
const MAC_CAPTION = MAC_RELEASED ? "" : "Coming soon to the Mac App Store";

const NAV = [
  ["Features", "features"], ["Online", "online"], ["Mac", "mac"],
  ["How It Works", "how"], ["Pricing", "pricing"], ["FAQ", "faq"],
];

const FEATURES = [
  { icon: Sparkles, title: "AI Assistant", body: "Describe the result you want in plain English." },
  { icon: Wrench, title: "Manual Tools", body: "Find, replace, number, clean and transform names." },
  { icon: GitCompareArrows, title: "Diff Preview", body: "See every change before approving it." },
  { icon: FileCode2, title: "Safe Apply / Export", body: "Apply directly on Mac or export online." },
  { icon: FolderTree, title: "Recursive Folders", body: "Rename complete folder trees, not just files." },
];

const STEPS = [
  { icon: FolderOpen, title: "Import", body: "Choose a folder or paste a list of names. We'll load and analyze your items." },
  { icon: Wand2, title: "Rename", body: "Use the AI Assistant or the Manual Tools to define your rules in seconds." },
  { icon: Eye, title: "Review", body: "Preview exactly what will change. Nothing happens until you approve." },
  { icon: Download, title: "Apply or Export", body: "On Mac, apply the renames directly with Undo. Online, export the safe commands." },
];

const ONLINE_FEATURES = ["Open a folder or paste names", "AI Assistant", "Manual rename tools", "Recursive folder support", "Diff preview", "Collision detection", "Export safe rename commands", "Copy commands", "Works directly in the browser"];
const MAC_FEATURES = ["Native Mac folder access", "AI Assistant", "Full manual rename tools", "Direct Apply Renames", "Undo Last Rename", "Rename History", "Collision protection", "Current / After Rename tree", "No Terminal required"];

const COMPARE = [
  ["AI Assistant", true, true],
  ["Manual Rename", true, true],
  ["Recursive Folders", true, true],
  ["Diff Preview", true, true],
  ["Collision Protection", true, true],
  ["Saved Recipes", true, true],
  ["Direct Apply Rename", false, true],
  ["Undo Rename", false, true],
  ["Rename History", "Limited", true],
  ["Script Export", true, "Optional"],
  ["Installation Required", "No", "Yes"],
];

const FAQS = [
  ["Is the online version just a demo?", "No. Rename Studio AI Online is a full product — AI assistant, every manual tool, recursive import, diff preview and collision protection. It exports safe rename commands rather than touching disk directly."],
  ["What's different about the Mac app?", "The Mac app renames files and folders directly on your device with one click, plus Undo Last Rename and Rename History — the most integrated experience. Online is the fastest way to start with no installation."],
  ["Which browsers work best online?", "Chrome and Edge support importing a whole folder tree. Any browser can paste a list of names."],
  ["Do I need an API key for the AI?", "No. The AI assistant works out of the box in both editions — just describe what you want."],
];

function Cell({ v }) {
  if (v === true) return <Check className="h-4 w-4 text-[#4ade80] mx-auto" />;
  if (v === false) return <Minus className="h-4 w-4 text-neutral-600 mx-auto" />;
  return <span className="text-xs text-neutral-400">{v}</span>;
}

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl bg-white/[0.03] overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors duration-200" data-testid={`faq-item-${idx}`}>
        <span className="flex-1 text-white font-medium">{q}</span>
        <ChevronDown className={`h-5 w-5 text-[#38bdf8] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-neutral-400 leading-relaxed">{a}</p>}
    </div>
  );
}

function AppWindowMock({ mode }) {
  const isMac = mode === "mac";
  return (
    <div className="rounded-2xl overflow-hidden border border-[#3b82f6]/30 bg-[#0a1020] shadow-[0_0_60px_rgba(59,130,246,0.25)]">
      <div className="flex items-center px-4 py-2.5 border-b border-white/10 bg-[#0b1226]">
        <div className="flex gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" /></div>
        <span className="mx-auto text-[11px] text-neutral-400 font-mono-ui">Rename Studio AI {isMac ? "for Mac" : "Online"}</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 text-[10px]">
        <div className="flex bg-black/40 rounded-full p-0.5 gap-0.5"><span className="px-3 py-1 rounded-full bg-[#3b82f6] text-white font-semibold">AI Assistant</span><span className="px-3 py-1 rounded-full text-neutral-200 border border-white/15 font-medium">Manual Tools</span></div>
        <span className="px-2 py-1 rounded-md border border-white/10 text-neutral-300">{isMac ? "Choose Folder" : "Open Folder"}</span>
        <span className="ml-auto px-2 py-1 rounded-md bg-[#00E5FF]/15 border border-[#00E5FF]/30 text-[#7fe9ff] font-medium">{isMac ? "Apply 12 Renames" : "Export rename.sh"}</span>
      </div>
      <div className="grid grid-cols-[1fr_1.4fr_auto] gap-2 px-4 py-2 border-b border-white/10 text-[9px] uppercase tracking-widest text-neutral-500"><span>Original</span><span>Preview</span><span>Type</span></div>
      <div className="divide-y divide-white/5">
        {[["Vacation Photo 1.jpg", "2024-05-24 — Vacation_Photo_1.jpg", "File"],
          ["IMG_1234 (copy).png", "2024-05-24 — IMG_1234_(copy).png", "File"],
          ["Project Final v2.docx", "2024-05-24 — Project_Final_v2.docx", "File"],
          ["Meeting Notes (final).pdf", "2024-05-24 — Meeting_Notes_(final).pdf", "File"],
          ["Q1 Reports", "2024-05-24 — Q1_Reports", "Folder"],
          ["Client Files", "2024-05-24 — Client_Files", "Folder"]].map(([o, p, t]) => (
          <div key={o} className="grid grid-cols-[1fr_1.4fr_auto] gap-2 px-4 py-2 text-[11px] font-mono-ui items-center">
            <span className="text-[#f87171]/80 truncate">{o}</span><span className="text-[#4ade80] truncate">{p}</span><span className="text-neutral-500">{t}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/10 text-[10px] text-neutral-500">
        <span className="text-neutral-300">12 changes</span>
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> {isMac ? "Nothing renames until you click Apply." : "Nothing changes until you export."}</span>
      </div>
    </div>
  );
}

const btnOnline = "bg-gradient-to-r from-[#22d3ee] to-[#3b82f6] shadow-[0_0_28px_rgba(34,211,238,0.4)]";
const btnMac = "bg-gradient-to-r from-[#6366f1] to-[#7c3aed] shadow-[0_0_28px_rgba(124,58,237,0.4)]";

export default function LandingPage() {
  const navigate = useNavigate();
  const [heroMode, setHeroMode] = useState("online");
  const [ctaOpen, setCtaOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);
  const openVideo = () => setShowVideo(true);
  const closeVideo = () => setShowVideo(false);

  useEffect(() => {
    const v = videoRef.current;
    if (showVideo) {
      document.body.style.overflow = "hidden";
      if (v) { v.currentTime = 0; v.muted = false; v.play?.().catch(() => {}); }
      const onKey = (e) => { if (e.key === "Escape") closeVideo(); };
      window.addEventListener("keydown", onKey);
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }
    if (v) { v.pause?.(); v.currentTime = 0; }
    document.body.style.overflow = "";
  }, [showVideo]);
  const goOnline = () => navigate("/app");
  const goMac = () => { if (MAC_RELEASED) window.open(MAC_APP_STORE_URL, "_blank"); else navigate("/app?edition=mac"); };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "radial-gradient(1200px 600px at 70% -10%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(900px 500px at 0% 20%, rgba(124,58,237,0.12), transparent 55%), #05070f" }}>
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#05070f]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center gap-6 px-6 py-4">
          <BrandLogo className="scale-[1.28] origin-left mr-2" />
          <nav className="hidden md:flex items-center gap-7 mx-auto">
            {NAV.map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} data-testid={`nav-${id}`} className="text-sm text-neutral-300 hover:text-white transition-colors duration-200">{label}</button>
            ))}
          </nav>
          <div className="relative ml-auto md:ml-0">
            <button onClick={() => setCtaOpen((o) => !o)} data-testid="nav-cta" className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-[#3b82f6] to-[#7c3aed] text-white hover:brightness-110 hover:scale-[1.03] transition-all duration-200 shadow-[0_0_24px_rgba(59,130,246,0.4)]">
              Try Rename Studio AI <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${ctaOpen ? "rotate-180" : ""}`} />
            </button>
            {ctaOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setCtaOpen(false)} />
                <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#0b1020] border border-white/15 shadow-2xl p-1.5 z-50" data-testid="nav-cta-menu">
                  <button onClick={goOnline} data-testid="nav-cta-online" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white rounded-lg hover:bg-white/10 transition-colors duration-200"><Globe className="h-4 w-4 text-[#22d3ee]" /> Use Online</button>
                  <button onClick={goMac} data-testid="nav-cta-mac" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white rounded-lg hover:bg-white/10 transition-colors duration-200"><Apple className="h-4 w-4 text-[#a78bfa]" /> {MAC_CTA}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-block text-[11px] font-bold tracking-widest uppercase text-[#93c5fd] border border-[#3b82f6]/40 rounded-full px-4 py-1.5 bg-[#3b82f6]/10">
            One Rename Studio · Online or Mac
          </span>
          <h1 className="font-display font-black text-5xl sm:text-6xl leading-[1.05] mt-6 tracking-tight">
            Rename files<br />and folders{" "}
            <span className="bg-gradient-to-r from-[#60a5fa] via-[#818cf8] to-[#38bdf8] bg-clip-text text-transparent">with AI</span>
          </h1>
          <p className="text-neutral-400 text-lg leading-relaxed mt-6 max-w-xl">
            Rename thousands of files and folders using plain English or powerful manual tools. Preview every change before anything happens. Use it instantly online or get the full Mac experience.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button onClick={goOnline} data-testid="hero-use-online" className={`flex items-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold text-white hover:brightness-110 hover:scale-[1.03] transition-all duration-200 ${btnOnline}`}>
              <Globe className="h-5 w-5" /> Use Online
            </button>
            <button onClick={goMac} data-testid="hero-download-mac" className={`flex items-center gap-2 rounded-xl px-6 py-3.5 text-base font-semibold text-white hover:brightness-110 hover:scale-[1.03] transition-all duration-200 ${btnMac}`}>
              <Apple className="h-5 w-5" /> {MAC_CTA}
            </button>
            <button onClick={openVideo} data-testid="hero-demo" className="flex items-center gap-2 rounded-xl px-5 py-3.5 text-base font-semibold text-neutral-300 hover:text-white transition-colors duration-200">
              <PlayCircle className="h-5 w-5" /> Watch Demo
            </button>
          </div>
          {!MAC_RELEASED && <p className="text-xs text-neutral-500 mt-3">{MAC_CAPTION} — preview the Mac experience now.</p>}
          <div className="flex flex-wrap gap-x-8 gap-y-4 mt-8">
            {[[Monitor, "Built for Mac", "Designed for Mac workflows"], [ShieldCheck, "Safe by Design", "Preview every change first"], [Zap, "Blazing Fast", "Handles thousands of files"]].map(([Icon, t, s]) => (
              <div key={t} className="flex items-center gap-3"><Icon className="h-5 w-5 text-[#38bdf8]" /><div><div className="text-sm font-semibold text-white">{t}</div><div className="text-xs text-neutral-500">{s}</div></div></div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }}>
          <div className="flex justify-center mb-3">
            <div className="flex bg-black/40 border border-white/10 rounded-full p-0.5" data-testid="hero-mode-toggle">
              {[["online", "Online App", Globe], ["mac", "Mac App", Apple]].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setHeroMode(k)} data-testid={`hero-mode-${k}`} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 ${heroMode === k ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:text-white"}`}><Icon className="h-3.5 w-3.5" /> {l}</button>
              ))}
            </div>
          </div>
          <AppWindowMock mode={heroMode} />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#3b82f6]/25 bg-[#3b82f6]/[0.06] px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-[#60a5fa]" /> AI Assistant</div>
              <p className="text-xs text-neutral-400 mt-1">Tell AI what you want.</p>
            </div>
            <div className="rounded-xl border border-white/15 bg-white/[0.03] px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-white"><Wrench className="h-4 w-4 text-neutral-300" /> Manual Tools</div>
              <p className="text-xs text-neutral-400 mt-1">Build the rename manually.</p>
            </div>
          </div>
          <p className="text-center text-xs text-neutral-500 mt-2">Both operate on the same live preview.</p>
        </motion.div>
      </section>

      {/* Real rename example */}
      <section className="max-w-5xl mx-auto px-6 pb-8 -mt-2">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 grid gap-6 md:grid-cols-2 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Before</div>
            <div className="font-mono-ui text-base break-all">
              <span className="line-through text-[#f87171]">32735_</span>
              <span className="text-neutral-300">Lolita_Taylor</span>
              <span className="line-through text-[#f87171]">_watermarked</span>
              <span className="text-neutral-300">.mp4</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-5 mb-2">After</div>
            <div className="font-mono-ui text-base"><span className="text-[#4ade80] font-semibold">Lolita Taylor</span><span className="text-neutral-300">.mp4</span></div>
          </div>
          <div className="rounded-2xl border border-[#3b82f6]/25 bg-[#3b82f6]/[0.06] p-5">
            <div className="flex items-center gap-2 text-[#60a5fa] text-xs font-semibold mb-2"><Sparkles className="h-4 w-4" /> AI instruction</div>
            <p className="text-white font-mono-ui text-sm leading-relaxed">"Rename the parent folder to Lolita Taylor too."</p>
            <div className="mt-4 flex items-center gap-2 text-sm font-mono-ui"><FolderTree className="h-4 w-4 text-[#60a5fa]" /><span className="text-neutral-400 line-through">Shoot</span><ArrowRight className="h-3.5 w-3.5 text-[#3b82f6]" /><span className="text-[#4ade80] font-semibold">Lolita Taylor</span></div>
            <p className="text-xs text-neutral-500 mt-4">Understands your whole folder tree — not just find-and-replace.</p>
          </div>
        </div>
      </section>

      {/* Use It Your Way */}
      <section id="online" className="max-w-7xl mx-auto px-6 py-16">
        <p className="text-center text-xs font-bold tracking-[0.3em] uppercase text-[#60a5fa] mb-3">Use It Your Way</p>
        <h2 className="text-center font-display font-black text-4xl mb-12">One Rename Studio. Online or Mac.</h2>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Online card */}
          <div className="rounded-3xl border border-[#22d3ee]/30 bg-white/[0.03] p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-2"><div className="h-11 w-11 rounded-xl bg-[#22d3ee]/15 border border-[#22d3ee]/30 flex items-center justify-center"><Globe className="h-5 w-5 text-[#22d3ee]" /></div><h3 className="font-display font-black text-2xl">Rename Studio Online</h3></div>
            <p className="text-neutral-400 leading-relaxed mb-6">No installation required. Open Rename Studio AI in Chrome or Edge and start working immediately. The fastest way to start.</p>
            <div className="space-y-2.5 flex-1">
              {ONLINE_FEATURES.map((f) => <div key={f} className="flex items-center gap-3 text-sm text-neutral-300"><Check className="h-4 w-4 text-[#22d3ee] shrink-0" /> {f}</div>)}
            </div>
            <button onClick={goOnline} data-testid="online-cta" className={`mt-8 w-full flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnOnline}`}>Launch Online App <ArrowRight className="h-5 w-5" /></button>
          </div>
          {/* Mac card */}
          <div id="mac" className="rounded-3xl border border-[#7c3aed]/30 bg-white/[0.03] p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-2"><div className="h-11 w-11 rounded-xl bg-[#7c3aed]/15 border border-[#7c3aed]/30 flex items-center justify-center"><Apple className="h-5 w-5 text-[#a78bfa]" /></div><h3 className="font-display font-black text-2xl">Rename Studio for Mac</h3></div>
            <p className="text-neutral-400 leading-relaxed mb-6">The full native Mac experience. Download Rename Studio AI for macOS and rename files and folders directly after reviewing your changes — the best integrated workflow.</p>
            <div className="space-y-2.5 flex-1">
              {MAC_FEATURES.map((f) => <div key={f} className="flex items-center gap-3 text-sm text-neutral-300"><Check className="h-4 w-4 text-[#a78bfa] shrink-0" /> {f}</div>)}
            </div>
            <button onClick={goMac} data-testid="mac-cta" className={`mt-8 w-full flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnMac}`}><Apple className="h-5 w-5" /> {MAC_CTA}</button>
            {!MAC_RELEASED && <p className="text-center text-xs text-neutral-500 mt-3">{MAC_CAPTION} · preview it online today</p>}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1226] to-[#0a0f1e] overflow-hidden" data-testid="comparison-table">
          <div className="grid grid-cols-[1.6fr_1fr_1fr] px-6 py-4 border-b border-white/10 text-sm">
            <span className="text-neutral-400 font-medium">Feature</span>
            <span className="text-center font-semibold text-[#22d3ee] flex items-center justify-center gap-1.5"><Globe className="h-4 w-4" /> Online</span>
            <span className="text-center font-semibold text-[#a78bfa] flex items-center justify-center gap-1.5"><Apple className="h-4 w-4" /> Mac</span>
          </div>
          {COMPARE.map(([label, o, m], i) => (
            <div key={label} className={`grid grid-cols-[1.6fr_1fr_1fr] px-6 py-3 items-center ${i % 2 ? "bg-white/[0.02]" : ""}`}>
              <span className="text-sm text-neutral-200">{label}</span>
              <div className="text-center"><Cell v={o} /></div>
              <div className="text-center"><Cell v={m} /></div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#3b82f6]/40 hover:bg-[#3b82f6]/[0.05] transition-colors duration-200">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#3b82f6]/20 to-[#7c3aed]/20 border border-[#3b82f6]/30 flex items-center justify-center mb-4"><f.icon className="h-5 w-5 text-[#60a5fa]" /></div>
              <h3 className="font-display font-bold text-white text-lg mb-1.5">{f.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>
      <section id="how" className="max-w-7xl mx-auto px-6 py-14">
        <p className="text-center text-xs font-bold tracking-[0.3em] uppercase text-[#60a5fa] mb-10">How It Works</p>
        <div className="grid gap-4 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#3b82f6]/20 to-[#7c3aed]/20 border border-[#3b82f6]/30 flex items-center justify-center mb-4"><s.icon className="h-6 w-6 text-[#60a5fa]" /></div>
              <h3 className="font-display font-bold text-white text-lg mb-1.5">{s.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{s.body}</p>
              {i < STEPS.length - 1 && <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3b82f6]/50" />}
            </div>
          ))}
        </div>

        <button onClick={openVideo} data-testid="how-demo-card" className="group mt-10 mx-auto block w-full max-w-2xl rounded-2xl border border-white/10 overflow-hidden relative hover:border-[#3b82f6]/50 transition-colors duration-200">
          <img src="/videos/demo-poster.jpg" alt="Rename Studio AI demo" className="w-full aspect-video object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-200" />
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
            <div className="h-16 w-16 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <Play className="h-7 w-7 text-white fill-white ml-1" />
            </div>
            <span className="mt-3 text-white font-semibold">See Rename Studio AI in Action</span>
            <span className="text-xs text-neutral-300">10-second demo</span>
          </div>
        </button>
      </section>

      {/* Safety band */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1226] to-[#0a0f1e] p-8 text-center">
          <ShieldCheck className="h-10 w-10 text-[#38bdf8] mx-auto mb-4" />
          <h3 className="font-display font-black text-2xl mb-3">Your files are never renamed until you approve the final preview.</h3>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 text-sm text-neutral-400 mt-4">
            <span className="flex items-center gap-2"><Apple className="h-4 w-4 text-[#a78bfa]" /> <b className="text-neutral-200">Mac:</b> Apply directly with built-in Undo.</span>
            <span className="flex items-center gap-2"><Globe className="h-4 w-4 text-[#22d3ee]" /> <b className="text-neutral-200">Online:</b> Preview first, then export the rename operation.</span>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-3xl mx-auto px-6 py-14 text-center">
        <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#60a5fa] mb-4">Pricing</p>
        <h2 className="font-display font-black text-4xl mb-4">Free while in beta</h2>
        <p className="text-neutral-400 leading-relaxed mb-8">Rename unlimited files and folders, use the AI assistant and every manual tool, online or on Mac — no account, no credit card.</p>
        <div className="rounded-3xl border border-[#3b82f6]/30 bg-white/[0.03] p-8 text-left max-w-md mx-auto">
          <div className="flex items-baseline gap-2 mb-6"><span className="font-display font-black text-5xl">$0</span><span className="text-neutral-500">/ forever in beta</span></div>
          {["AI + Manual renaming", "Recursive folder import", "Diff preview & conflict detection", "Online export + Mac direct apply & undo"].map((t) => (
            <div key={t} className="flex items-center gap-3 py-2 text-sm text-neutral-300"><Check className="h-4 w-4 text-[#4ade80]" /> {t}</div>
          ))}
          <div className="flex gap-2 mt-6">
            <button onClick={goOnline} data-testid="pricing-online" className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnOnline}`}><Globe className="h-4 w-4" /> Use Online</button>
            <button onClick={goMac} data-testid="pricing-mac" className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnMac}`}><Apple className="h-4 w-4" /> Mac</button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-center text-xs font-bold tracking-[0.3em] uppercase text-[#60a5fa] mb-10">FAQ</p>
        <div className="space-y-3">{FAQS.map(([q, a], i) => <Faq key={q} q={q} a={a} idx={i} />)}</div>
      </section>

      {/* Video modal */}
      {showVideo && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={closeVideo} data-testid="video-modal">
          <div className="w-full max-w-[1040px]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-black text-lg sm:text-xl bg-gradient-to-r from-[#60a5fa] via-[#818cf8] to-[#38bdf8] bg-clip-text text-transparent">Rename Studio AI in 10 Seconds</h3>
              <button onClick={closeVideo} data-testid="video-close" className="h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors duration-200"><X className="h-5 w-5" /></button>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#3b82f6]/30 shadow-[0_0_80px_rgba(59,130,246,0.35)] bg-black">
              <video
                ref={videoRef}
                data-testid="demo-video"
                className="w-full aspect-video bg-black"
                src="/videos/rename-studio-ai-demo.mp4"
                poster="/videos/demo-poster.jpg"
                controls
                playsInline
              />
            </div>
            <div className="flex items-center justify-center gap-3 mt-4">
              <button onClick={goOnline} data-testid="video-online" className={`flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnOnline}`}><Globe className="h-5 w-5" /> Use Online</button>
              <button onClick={goMac} data-testid="video-mac" className={`flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white hover:brightness-110 transition-all duration-200 ${btnMac}`}><Apple className="h-5 w-5" /> {MAC_CTA}</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center gap-6">
          <BrandLogo />
          <p className="text-sm text-neutral-500 sm:ml-auto">© {new Date().getFullYear()} Rename Studio AI — One studio, two ways to use it.</p>
          <div className="flex items-center gap-3">
            <button onClick={goOnline} className="flex items-center gap-1.5 text-sm font-semibold text-[#22d3ee] hover:text-white transition-colors duration-200" data-testid="footer-online"><Globe className="h-4 w-4" /> Online</button>
            <button onClick={goMac} className="flex items-center gap-1.5 text-sm font-semibold text-[#a78bfa] hover:text-white transition-colors duration-200" data-testid="footer-mac"><Apple className="h-4 w-4" /> Mac</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
